import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { UploadIcon, XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { ImportGuide } from '../../../../components/pages/backoffice/import-guide.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../../components/table/data-table-column-header.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../../components/ui/alert-dialog.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import { Combobox } from '../../../../components/ui/combobox.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { getImportFile } from '../../../../utils/files.js';

const TABLE_COLUMNS = [
  {
    title: 'Número',
    accessorKey: 'number',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('number')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Nome',
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    cell: ({ row }) => {
      return <div className="lg:w-64">{row.getValue('name')}</div>;
    },
    enableSorting: true,
    enableHiding: false,
  },
];

export function ShopsImport() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [previewTableData, setPreviewTableData] = useState([]);
  const [mapping, setMapping] = useState({
    values: {}, // objecto { [coluna_catalyst]: [coluna_excel] }
    headers: [], // array de objectos ( label, value ) -> nome das colunas do excel
  });
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [confirmImportation, setConfirmImportation] = useState(false);

  const table = useReactTable({
    data: previewTableData,
    columns: TABLE_COLUMNS,
    rowCount: previewTableData.length,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const formShopsImportation = useForm({
    defaultValues: {
      importFile: '',
      deleteCurrentRecords: false,
    },
    resolver: yupResolver(
      yup.object().shape({
        importFile: yup
          .mixed()
          .test('required', 'O Ficheiro é um campo obrigatório', (file) => {
            if (file && file.size) return true;
            return false;
          })
          .test(
            'fileType',
            'O ficheiro inserido não é um ficheiro CSV ou XLXS',
            (value) => {
              return (
                (value &&
                  value.name.toLowerCase().split('.').pop() === 'csv') ||
                value.name.toLowerCase().split('.').pop() === 'xlsx'
              );
            },
          ),
        deleteCurrentRecords: yup.boolean().required(),
      }),
    ),
  });

  const watchImportFileField = formShopsImportation.watch('importFile');
  const watchDeleteCurrentRecordsField = formShopsImportation.watch(
    'deleteCurrentRecords',
  );

  const loadFile = async (file) => {
    try {
      const response = await getImportFile(file);
      setMapping({ headers: response.headers, values: {} });
      setPreviewTableData(response.data.slice(0, 10));
      setData(response.data);
    } catch (err) {
      toast.error('Erro ao carregar ficheiro');
    }
  };

  const onMappingFieldsChange = (item, key) => {
    const newData = data.map((y) => ({ ...y, [key]: y[item.value] }));
    const newPreviewData = previewTableData.map((y) => ({
      ...y,
      [key]: y[item.value],
    }));

    setData(newData);
    setPreviewTableData(newPreviewData);

    setMapping((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: item.value },
    }));
  };

  const mutationImportShops = useMutation({
    mutationFn: async (data) => {
      console.log('data', data);
      await msCatalystApi.post('/shops/import', {
        data: data.newData,
        deleteCurrentRecords: data.deleteCurrentRecords,
      });

      return true;
    },
    onSuccess: (data) => {
      toast.success('Importação de Lojas concluída com sucesso!');

      navigate('/backoffice/admin/shops');
    },
    onError: (err) => {
      if (
        err?.response?.data?.message ===
        'Duplicate value for number. Please give a different value'
      ) {
        toast.error(`Já existe uma lojá com esse número`, {
          description: 'Por favor, altere o número de loja',
        });
      } else {
        console.log(err);
        toast.error('Ocorreu um erro na importação de Lojas...', {
          description: 'Tente novamente mais tarde',
        });
      }

      setIsAlertDialogOpen(false);
    },
  });

  const onFormSubmit = async (inputs) => {
    // clear keys
    console.log('inputs', inputs);

    const newData = data.map((item) => {
      let newItem = {};
      TABLE_COLUMNS.map((c) => c.accessorKey).forEach((key) => {
        newItem[key] = item[key];
      });
      return newItem;
    });

    mutationImportShops.mutate({
      newData: newData,
      deleteCurrentRecords: inputs.deleteCurrentRecords,
    });
  };

  return (
    <>
      <Heading className="pb-6">Importar Lojas</Heading>

      <Form {...formShopsImportation}>
        <form onSubmit={formShopsImportation.handleSubmit(onFormSubmit)}>
          <ImportGuide />

          <div className="flex flex-col space-y-10 pt-10">
            <FormCard className="xl:grid-cols-3">
              <FormField
                control={formShopsImportation.control}
                name="importFile"
                render={({ field }) => (
                  <FormItem className="col-span-1 lg:col-span-2 xl:col-span-3">
                    <FormLabel required={true}>Ficheiro</FormLabel>
                    <FormControl>
                      <Input
                        type="file"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        onChange={(ev) => {
                          const files = ev.target.files;
                          if (files.length > 0) loadFile(files[0]);

                          formShopsImportation.setValue('importFile', files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {TABLE_COLUMNS.map((item, index) => (
                <FormItem key={index}>
                  <FormLabel required={true}>{item.title}</FormLabel>
                  <Combobox
                    items={mapping.headers}
                    value={mapping?.values?.[item.accessorKey]}
                    valueKey="value"
                    labelKey="label"
                    disabled={watchImportFileField !== '' ? false : true}
                    onChange={(value) =>
                      onMappingFieldsChange(value, item.accessorKey)
                    }
                  />
                </FormItem>
              ))}

              <FormField
                control={formShopsImportation.control}
                name="deleteCurrentRecords"
                render={({ field }) => (
                  <FormItem className="col-span-1 lg:col-span-2 xl:col-span-3">
                    <div className="flex h-10 flex-row items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          disabled={watchImportFileField !== '' ? false : true}
                          id="deleteCurrentRecords"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                      <FormLabel className="space-y-0">
                        Apagar todos os registos existentes
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchImportFileField !== '' ? (
                <>
                  <hr className="col-span-1 mx-auto my-3 h-0.5 w-3/4 border-t-0 bg-neutral-100 dark:bg-white/10 lg:col-span-2 xl:col-span-3" />

                  <div className="col-span-1 flex flex-col lg:col-span-2 xl:col-span-3">
                    <p className="pb-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed">
                      Pré-visualização dos dados
                    </p>
                    <p className="text-xs leading-none text-stone-500 peer-disabled:cursor-not-allowed">
                      (a pré-visualizar {previewTableData.length} registo(s) de{' '}
                      {data.length})
                    </p>

                    <div className="mt-5 rounded-md border border-muted-foreground/35 bg-background">
                      <Table>
                        <TableHeader>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                return (
                                  <TableHead
                                    key={header.id}
                                    colSpan={header.colSpan}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                  </TableHead>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableHeader>

                        <TableBody>
                          {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                              <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                  <TableCell key={cell.id}>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={TABLE_COLUMNS.length}
                                className="h-24 text-center"
                              >
                                Não foram encontrados resultados.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              ) : null}
            </FormCard>
          </div>

          <FormFooter>
            <CancelButton />

            {watchDeleteCurrentRecordsField ? (
              <AlertDialog
                open={isAlertDialogOpen}
                onOpenChange={setIsAlertDialogOpen}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    disabled={
                      !mapping.values ||
                      Object.keys(mapping?.values)?.length !==
                        TABLE_COLUMNS.length
                    }
                  >
                    <UploadIcon />
                    Importar
                  </Button>
                </AlertDialogTrigger>

                <AlertDialogContent>
                  {mutationImportShops.isPending ? (
                    <div className="flex w-full justify-center">
                      <Loading message="A importar Lojas... Por favor aguarde" />
                    </div>
                  ) : (
                    <>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-bold text-red-500">
                          Importar Lojas e Apagar Registos Anteriores
                        </AlertDialogTitle>
                        <AlertDialogDescription className="mt-0 text-lg font-bold text-red-500">
                          Tem a certeza? Esta acção é irreversível!
                        </AlertDialogDescription>
                      </AlertDialogHeader>

                      <div className="flex flex-row items-start space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mt-0.5"
                            checked={confirmImportation}
                            onCheckedChange={(checked) =>
                              setConfirmImportation(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="space-y-0 text-sm">
                          Eu tenho conhecimento que esta ação tem consequências
                          irreversíveis, eliminando todos os registos existentes
                          neste módulo, e que mesmo assim quero realizá-la.
                        </FormLabel>
                      </div>
                      <FormMessage />

                      <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel type="button">
                          <XIcon />
                          Cancelar
                        </AlertDialogCancel>
                        <Button
                          variant="destructive"
                          disabled={!confirmImportation}
                          onClick={async (event) => {
                            formShopsImportation.handleSubmit(onFormSubmit)();
                          }}
                        >
                          <UploadIcon />
                          Importar
                        </Button>
                      </AlertDialogFooter>
                    </>
                  )}
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button
                type="submit"
                disabled={
                  !mapping.values ||
                  Object.keys(mapping?.values)?.length !== TABLE_COLUMNS.length
                }
              >
                <UploadIcon />
                Importar
              </Button>
            )}
          </FormFooter>
        </form>
      </Form>
    </>
  );
}
